/* Notification Center — the Escalation fields, rendered inside the Delivery Method In-App alert group (Rule
 * Detail → Delivery Method and the New Rule wizard's Delivery Method step — owner request 2026-10-23; until then
 * a tab / step of their own), so the two can never drift.
 *
 * One sentence (owner request 2026-10-24): "☐ Escalate — If nobody acknowledges within [1 hour], also send it to
 * [users]", plus one line saying exactly who gets it and how. The users are individual active users shown as
 * "Role - User name" (shared CheckedCombo, several allowed). The caller shows it only when escalation can work
 * (escAvailable). Controlled: the caller owns the value and saves it (AI_NotificationRuleEscalationUser +
 * AI_NotificationRule.EscalateAfter); unticking clears the users. A rule set up before users could be chosen
 * keeps its Recipient Group, shown as such, until users replace it. */
import { useEffect, useMemo, useState } from 'react';
import { CheckedCombo } from '../../components/CheckedCombo';
import type { RecipientUser } from './api';
import type { NcEscalateAfter } from './data';
import type { DeliveryValue } from './deliveryMethod';
import {
  ESCALATE_AFTER_LABEL, escGroupName, escIsOn, escNote, escUserLabel, loadEscalationUsers, peekEscalationUsers,
  type EscalationValue,
} from './escalation';
import { useNcStore } from './store';

export function EscalationFields({ value, onChange, delivery, disabled = false }: {
  value: EscalationValue;
  onChange: (next: EscalationValue) => void;
  /** The rule's channels and Business hours — named in the summary line. */
  delivery: Pick<DeliveryValue, 'email' | 'bizHours'>;
  /** Read-only (Rule Detail in View mode). */
  disabled?: boolean;
}) {
  const groups = useNcStore((s) => s.recipients);
  const [users, setUsers] = useState<RecipientUser[]>(peekEscalationUsers);
  const [loading, setLoading] = useState(users.length === 0);
  useEffect(() => {
    let alive = true;
    loadEscalationUsers()
      .then((rows) => { if (alive) setUsers(rows); })
      // The list stays empty; the picker shows no options and the saved users still save unchanged.
      .catch(() => { /* surfaced by the shared API client's error handling */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Ticked = escalation wanted. Saved users (or a legacy group) tick it; a Cancel / save back to view mode re-reads it.
  const saved = escIsOn(value);
  const [on, setOn] = useState(saved);
  useEffect(() => { if (saved || disabled) setOn(saved); }, [saved, disabled]);
  const off = disabled || !on;

  // Label ⇄ id. A chosen user who is no longer active stays listed (so it can be removed), never silently dropped.
  const { options, idByLabel, labelById } = useMemo(() => {
    const byLabel = new Map<string, string>();
    const byId = new Map<string, string>();
    const add = (id: string, base: string) => {
      let label = base;
      for (let n = 2; byLabel.has(label); n++) label = `${base} (${n})`;
      byLabel.set(label, id); byId.set(id, label);
    };
    for (const u of users) add(u.userId, escUserLabel(u));
    for (const id of value.userIds) if (!byId.has(id)) add(id, 'Inactive user');
    return { options: [...byLabel.keys()], idByLabel: byLabel, labelById: byId };
  }, [users, value.userIds]);

  return (
    <div className="nc-esc-fields">
      <div className="nc-esc-line">
        <label className="nc-esc-chk">
          <input type="checkbox" checked={on} disabled={disabled}
            onChange={(e) => { setOn(e.target.checked); if (!e.target.checked && saved) onChange({ ...value, userIds: [], groupId: '' }); }} />
          <strong>Escalate</strong>
        </label>
        <span className={off ? 'nc-esc-muted' : undefined}>If nobody acknowledges within</span>
        <select id="nc-esc-after" aria-label="Escalate after" className="nc-fselect nc-esc-after" disabled={off} value={value.after}
          onChange={(e) => onChange({ ...value, after: e.target.value as NcEscalateAfter })}>
          {(Object.keys(ESCALATE_AFTER_LABEL) as NcEscalateAfter[]).map((k) => <option key={k} value={k}>{ESCALATE_AFTER_LABEL[k]}</option>)}
        </select>
        <span className={off ? 'nc-esc-muted' : undefined}>, also send it to</span>
        <div className="nc-esc-users">
          <CheckedCombo ariaLabel="Escalate to" options={options} disabled={off} loading={loading && !users.length}
            values={value.userIds.map((id) => labelById.get(id) ?? 'Inactive user')}
            placeholder="Choose users…" unit="users" summaryJoin=", "
            // Choosing users replaces a Recipient Group set up before users could be chosen.
            onChange={(labels) => onChange({ ...value, groupId: '', userIds: labels.map((l) => idByLabel.get(l)).filter((x): x is string => !!x) })} />
        </div>
      </div>
      {on && value.groupId && !value.userIds.length && (
        <div className="nc-panel-sub nc-esc-note">Set up before users could be chosen: it escalates to the group “{escGroupName(value.groupId, groups)}”. Choose users to replace it.</div>
      )}
      {on && <div className="nc-panel-sub nc-esc-note">{escNote(value, users, groups, delivery)}</div>}
    </div>
  );
}
